import { CheckCircle2, Loader2, X } from 'lucide-react';
import { useCallback, useMemo } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useGetCompanionCoveragesReviewQuery } from '@/services/api/coverageService';
import type { CompanionMemberCoverages } from '@/services/types/coverage-types';
import { CoverageCategory, CoverageStatus } from '@/services/types/coverage-types';

import { CompanionMatterSection } from './CompanionMatterSection';
import { PrimaryCoverageCard } from './PrimaryCoverageCard';
import { useCompanionAssociations } from './useCompanionAssociations';

/** Only show 1P and 3P coverages in the review modal */
const REVIEWABLE_CATEGORIES = new Set<CoverageCategory | null>([
  CoverageCategory.FIRST_PARTY,
  CoverageCategory.THIRD_PARTY,
]);

/** Sort order: 1P before 3P, then everything else */
const CATEGORY_SORT_ORDER: Record<string, number> = {
  [CoverageCategory.FIRST_PARTY]: 0,
  [CoverageCategory.THIRD_PARTY]: 1,
};

interface ReviewGroupCoveragesModalProps {
  isOpen: boolean;
  onClose: () => void;
  matterId: string;
}

export const ReviewGroupCoveragesModal = ({
  isOpen,
  onClose,
  matterId,
}: ReviewGroupCoveragesModalProps) => {
  const { data, isLoading, isError } = useGetCompanionCoveragesReviewQuery(
    { matterId },
    { skip: !isOpen },
  );

  const {
    associations,
    isCompleting,
    completionResult,
    clearCompletionResult,
    handleToggleAssociation,
    handleComplete,
  } = useCompanionAssociations(data, matterId);

  const filterCoverages = useCallback(
    (member: CompanionMemberCoverages): CompanionMemberCoverages => ({
      ...member,
      coverages: member.coverages
        .filter(
          (c) =>
            REVIEWABLE_CATEGORIES.has(c.category) &&
            c.status !== CoverageStatus.DUPLICATE,
        )
        .sort(
          (a, b) =>
            (CATEGORY_SORT_ORDER[a.category ?? ''] ?? 99) -
            (CATEGORY_SORT_ORDER[b.category ?? ''] ?? 99),
        ),
    }),
    [],
  );

  const primaryMember = useMemo(() => {
    const raw = data?.members.find((m) => m.is_primary);
    return raw ? filterCoverages(raw) : null;
  }, [data?.members, filterCoverages]);

  const companionMembers = useMemo(
    () =>
      (data?.members.filter((m) => !m.is_primary) ?? []).map(filterCoverages),
    [data?.members, filterCoverages],
  );

  const handleClose = () => {
    if (!isCompleting) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        hideCloseButton
        className="max-w-5xl max-h-[85vh] flex flex-col border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-0 shadow-xl"
      >
        {/* Header */}
        <DialogHeader className="flex-shrink-0 border-b border-zinc-200 dark:border-zinc-800 px-6 py-3">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-base font-semibold text-zinc-900 dark:text-white">
                Review Group Coverages
              </DialogTitle>
              <DialogDescription className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                Review and share coverages across companion cases
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleClose}
                variant="ghost"
                size="sm"
                className="h-8 px-3 text-sm font-medium text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
              >
                Close
              </Button>
              <Button
                onClick={handleComplete}
                disabled={isCompleting || isLoading || isError}
                size="sm"
                className="h-8 bg-indigo-600 px-4 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isCompleting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update Coverages'
                )}
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4">
          {completionResult && (
            <div className="rounded-md border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2 -mt-1 mb-5">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                  <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300">
                    Shared Coverages Updated
                  </p>
                </div>
                <button
                  type="button"
                  onClick={clearCompletionResult}
                  className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-200 transition-colors"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}

          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-zinc-200 dark:border-zinc-700 border-t-indigo-600 dark:border-t-indigo-400" />
              <span className="ml-2 text-sm font-medium text-zinc-500 dark:text-zinc-400">
                Loading coverages...
              </span>
            </div>
          )}

          {isError && (
            <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
              <p className="text-sm font-medium text-red-800 dark:text-red-300">
                Failed to load companion coverages. Please try again.
              </p>
            </div>
          )}

          {!isLoading && !isError && data && (
            <div className="grid grid-cols-2 gap-6">
              {/* Left column: Primary matter */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-zinc-900 dark:text-white">
                    {primaryMember?.matter_name ?? 'Primary Matter'}
                  </p>
                  <Badge
                    variant="outline"
                    className="text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800"
                  >
                    Primary
                  </Badge>
                </div>

                {primaryMember && primaryMember.coverages.length > 0 ? (
                  <div className="space-y-2">
                    {primaryMember.coverages.map((coverage) => (
                      <PrimaryCoverageCard
                        key={coverage.id}
                        coverage={coverage}
                        companionMatters={companionMembers}
                        sharedMatterIds={
                          associations.get(coverage.id) ?? new Set()
                        }
                        onToggleAssociation={handleToggleAssociation}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm font-medium text-zinc-500 dark:text-zinc-400 py-4">
                    No coverages found for primary matter
                  </p>
                )}
              </div>

              {/* Right column: Companion matters */}
              <div className="space-y-3">
                <p className="text-sm font-semibold text-zinc-900 dark:text-white">
                  Companion Matters
                </p>

                {companionMembers.length > 0 ? (
                  <div className="space-y-2">
                    {companionMembers.map((member) => (
                      <CompanionMatterSection
                        key={member.matter_id}
                        member={member}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm font-medium text-zinc-500 dark:text-zinc-400 py-4">
                    No companion matters found
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
